<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { ChevronDown } from 'lucide-vue-next'

interface DropdownItem {
  key: string
  label: string
  icon?: unknown
  danger?: boolean
  disabled?: boolean
  divided?: boolean
}

interface Props {
  modelValue?: boolean
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  trigger?: 'click' | 'hover'
  items: DropdownItem[]
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'bottom-start',
  trigger: 'click',
  disabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  select: [key: string]
}>()

const open = ref(props.modelValue ?? false)
const root = ref<HTMLElement | null>(null)

function toggle(): void {
  if (props.disabled) return
  open.value = !open.value
  emit('update:modelValue', open.value)
}

function close(): void {
  open.value = false
  emit('update:modelValue', false)
}

function selectItem(item: DropdownItem): void {
  if (item.disabled) return
  emit('select', item.key)
  close()
}

function onClickOutside(e: MouseEvent): void {
  if (!open.value) return
  const target = e.target as Node
  if (root.value && !root.value.contains(target)) close()
}

onMounted(() => {
  document.addEventListener('click', onClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
})

const placementClass: Record<NonNullable<Props['placement']>, string> = {
  'bottom-start': 'left-0 top-full mt-1',
  'bottom-end':   'right-0 top-full mt-1',
  'top-start':    'left-0 bottom-full mb-1',
  'top-end':      'right-0 bottom-full mb-1',
}
</script>

<template>
  <div ref="root" class="relative inline-block">
    <button
      type="button"
      class="btn-base h-9 px-3 text-sm bg-bg-elevated text-fg hover:bg-bg-hover border border-border"
      :disabled="disabled"
      @click="toggle"
    >
      <slot />
      <ChevronDown :size="14" :class="open ? 'rotate-180 transition-transform' : 'transition-transform'" />
    </button>
    <transition name="menu">
      <ul
        v-if="open"
        class="absolute z-40 min-w-[10rem] rounded-md border border-border bg-bg-surface py-1 shadow-lg"
        :class="placementClass[placement]"
        role="menu"
      >
        <template v-for="item in items" :key="item.key">
          <li v-if="item.divided" class="my-1 border-t border-border" />
          <li>
            <button
              type="button"
              role="menuitem"
              class="flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors duration-fast"
              :class="
                item.disabled
                  ? 'cursor-not-allowed text-fg-subtle'
                  : item.danger
                    ? 'text-danger hover:bg-danger/10'
                    : 'text-fg hover:bg-bg-hover'
              "
              :disabled="item.disabled"
              @click="selectItem(item)"
            >
              <component v-if="item.icon" :is="item.icon" :size="14" />
              <span>{{ item.label }}</span>
            </button>
          </li>
        </template>
      </ul>
    </transition>
  </div>
</template>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}
.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
